/**
 * The district's end of the grid, and the brownout ledger.
 *
 * The first of the five flows `DESIGN-GOVERNORS.md` §5 specifies, and the one
 * the design calls the most political object in the game:
 *
 * > Each polity runs one grid. Every season the governor holds a **power
 * > priority list** — an ordered allocation of generated MW across facilities
 * > and districts. When supply < demand, allocation runs down the list and
 * > stops. […] the consequences land as measurable brownout-hours in specific
 * > homes.
 *
 * A district does not generate power. It *receives* an allocation and spends it
 * on what stands on its map. So this class is two halves:
 *
 *   - **the meter** — demand, summed over buildings and lit cells, once per
 *     budget cycle
 *   - **the shedder** — what goes dark when the sum exceeds the supply, and the
 *     record of which building it was, for how many cycles
 *
 * ## Every number here is counted
 *
 * Law 1 (*no authored deltas*): demand is a sum over things a mayor built, and
 * the ledger is a sum over cycles that were actually short. Nothing is rolled,
 * nothing is scaled by a fudge factor, and there is no comfort, morale or
 * satisfaction term anywhere in the file. A district whose demand fits under its
 * supply is a district in which this class does nothing at all.
 *
 * The supply is a world parameter until there is a polity to set it
 * (`INTERCONNECT_KW`), and a *national input* once there is (`setAllocation`).
 * §4's sentence is the reason the distinction matters: a district's behaviour is
 * a pure function of *its committed map layout plus national inputs*, so the
 * allocation is state that travels in the snapshot rather than configuration
 * that travels with the build.
 *
 * ## The shed order is a policy, written once
 *
 * When the sum does not fit, something goes off, and *which* thing is a decision
 * the design leaves to the district. It is made here, once, in one array:
 *
 *     street lighting → construction sites → business premises → homes
 *                     → federal facilities
 *
 * A town turns off its conveniences before it stops its building, stops its
 * building before it darkens its shops, and darkens its shops before it darkens
 * the houses people live in. Homes going dark is therefore the *last* thing a
 * shortfall does to the town — and it is not the last thing the shortfall does,
 * because the facilities the polity built on this map come after it.
 *
 * That order is not a courtesy. §5 calls the power priority list *"the single
 * most political object in the game"* precisely because *"a data center's uptime
 * contract versus a district's lit homes is not a dilemma the game narrates — it
 * is a sort order the governor must literally write."* This build has no
 * governor, so the district writes it, and a district has no authority to switch
 * off a federal facility it never agreed to host. A mayor who wants that clause
 * has to negotiate for it (§7's worked exchange has one: *"uptime priority may
 * not outrank residential power in drought"*), and negotiation arrives in Phase
 * 3. Until then the last two entries in this array are the whole of the politics,
 * and they are pointing the way the design says they point by default.
 *
 * Within a class the burden **rotates**: one counter, advanced once per metered
 * cycle, decides where the walk down the id list starts. That is what a real
 * utility does with rotating outage blocks, and it is the difference between a
 * district where forty families are permanently dark and one where four hundred
 * families are each dark a tenth of the time. Neither is fair; only the second
 * is a brownout.
 *
 * ## What a brownout does today
 *
 * One thing, on the map: a shed construction site does not build that cycle
 * (§5 lists construction among power's consumers). Shed homes and premises are
 * recorded and rendered dark, and are otherwise not punished — the design puts
 * their consequence in quality of life and migration (§10), which is a later
 * Phase 2 mechanic and owns its own arithmetic. The ledger is built first
 * because migration will be a sum over it.
 *
 * A curtailed facility is recorded too, as a fraction rather than as a list, and
 * that fraction is the one term of §4's `min(power, water, staffing) × capacity`
 * this flow can supply. It buys nothing yet either — see `FacilityManager`.
 *
 * See `docs/flows.md`.
 */

import { Canonical } from '../hashState';

/**
 * Loads and supply, in kilowatts. World parameters, not state.
 *
 * A snapshot restored under different numbers is a different world (see
 * `core/snapshot.ts`), and the Charter (design §17) is where these eventually
 * live.
 */
export interface PowerConfig {
  /**
   * What the district may draw while no polity allocates it anything.
   *
   * The stand-in for the grid connection a district would have had before it
   * joined a polity, and the only reason this mechanic does anything in a
   * single-player world. It is deliberately a number a *growing* district
   * reaches: development raises demand, the interconnect does not move, and the
   * lights start going out on their own. Nothing decides that a brownout should
   * happen; a sum crosses a constant.
   */
  INTERCONNECT_KW: number;
  /** A family home's baseline draw. */
  HOUSEHOLD_KW: number;
  /** A corporation's baseline draw. Premises run harder than homes. */
  COMMERCIAL_KW: number;
  /** Street lighting, per painted cell. */
  STREETLIGHT_KW: number;
  /** A building under construction: the site's own power. */
  CONSTRUCTION_KW: number;
}

export const DEFAULT_POWER_CONFIG: PowerConfig = {
  INTERCONNECT_KW: 500,
  HOUSEHOLD_KW: 4,
  COMMERCIAL_KW: 15,
  STREETLIGHT_KW: 0.25,
  CONSTRUCTION_KW: 20,
};

/**
 * The four things that draw power, in the order they are shed.
 *
 * Exported because the order *is* the policy, and a reader looking for it should
 * find one list rather than a sequence of `if`s. Same move, and same reason, as
 * `SETTLEMENT_STAGE_ORDER` in `SeasonManager`.
 */
export const SHED_ORDER = Object.freeze([
  'streets',
  'sites',
  'premises',
  'homes',
  'facilities',
] as const);

export type PowerClass = (typeof SHED_ORDER)[number];

/**
 * What the district has on its map, as the meter sees it.
 *
 * A bag of ids and counts rather than a `SurfaceSim`, assembled by the caller.
 * The same discipline `SeasonManager` is held to and for a weaker reason — this
 * class writes nothing public — but it costs nothing and it keeps a flow from
 * quietly acquiring an opinion about traffic, bushes or ants.
 *
 * The three id arrays are in house creation order, which is stable across a
 * snapshot round-trip because `HouseManager` keeps its array in that order.
 */
export interface PowerReading {
  /** Buildings still going up. Shed second; a shed site does not build. */
  sites: number[];
  /** Built corporations. Shed third. */
  premises: number[];
  /** Built homes, vacant or not — an empty house still has a meter. Shed fourth. */
  homes: number[];
  /** Painted cells carrying street lighting. Shed first. */
  litCells: number;
  /**
   * What the polity's facilities on this map draw, in kW. Shed last, and in part.
   *
   * A total rather than a list of ids, because this flow does not ration between
   * facilities: it decides how much reached the estate, and `FacilityManager`
   * decides what that means to each of them. A district with no facility on it
   * hands in zero, which is every district in this repository's golden masters.
   */
  facilityKw: number;
}

/** Demand, broken out by class. Kilowatts. */
export interface PowerDemand {
  streets: number;
  sites: number;
  premises: number;
  homes: number;
  facilities: number;
  total: number;
}

/** The meter's complete state. See `PowerManager.save()`. */
export interface PowerSnapshot {
  allocationKw: number | null;
  rotation: number;
  demand: PowerDemand;
  supplyKw: number;
  shedKw: number;
  shedHouses: number[];
  litCellsShed: number;
  facilityShedKw: number;
  darkCycles: Array<[number, number]>;
  meteredCycles: number;
  shortfallCycles: number;
  deliveredKwCycles: number;
  shedKwCycles: number;
  darkHouseCycles: number;
  facilityShedKwCycles: number;
}

const NO_DEMAND: PowerDemand = {
  streets: 0, sites: 0, premises: 0, homes: 0, facilities: 0, total: 0,
};

/**
 * The district's grid connection.
 *
 * Owned by `SurfaceSim`, metered once per settled budget cycle, snapshotted
 * inside it. Holds no `Rng`: a shortfall is arithmetic, and which home is dark
 * is a rotation, so there is nothing here to roll.
 */
export class PowerManager {
  private config: PowerConfig;

  /**
   * What the polity above has allocated this district, in kW.
   *
   * `null` means nobody has: the district falls back to its own interconnect.
   * A national input (§4) and therefore *state* — two districts drawing the
   * same map under different allocations are different worlds, and one of them
   * has its lights on.
   */
  private allocationKw: number | null = null;

  /** Where the walk down each class's id list starts. Advanced once per cycle. */
  private rotation = 0;

  // --- The last metered cycle -----------------------------------------------

  private demand: PowerDemand = { ...NO_DEMAND };
  private supplyKw = 0;
  private shedKw = 0;
  private readonly shedHouses = new Set<number>();
  private litCellsShed = 0;
  /** Kilowatts the estate asked for and did not get. See `shedClass`. */
  private facilityShedKw = 0;

  // --- The ledger -----------------------------------------------------------

  /** Cycles each house has spent shed, by house id. Pruned when a house goes. */
  private readonly darkCycles = new Map<number, number>();

  private meteredCycles = 0;
  private shortfallCycles = 0;
  private deliveredKwCycles = 0;
  private shedKwCycles = 0;
  private darkHouseCycles = 0;
  private facilityShedKwCycles = 0;

  constructor(config: Partial<PowerConfig> = {}) {
    this.config = { ...DEFAULT_POWER_CONFIG, ...config };
  }

  getConfig(): PowerConfig {
    return { ...this.config };
  }

  updateConfig(partial: Partial<PowerConfig>): void {
    this.config = { ...this.config, ...partial };
  }

  // --- The national input ---------------------------------------------------

  /**
   * What the polity above allocates this district, or `null` for none.
   *
   * Nothing calls this yet from inside the simulation, which is the honest
   * state of a build with no polity in it. A scripted governor sets it (Phase 2
   * calls that a config file; here it is a method), and in Phase 3 it is the
   * result of a governor's `set_power_priority` running down its list.
   *
   * It takes effect at the next metered cycle rather than at the next season,
   * because a season boundary in this build reads the district and changes
   * nothing about it (ADR-012 D63). Latching an allocation for the length of a
   * season is the polity's job and arrives with the polity.
   */
  setAllocation(kw: number | null): void {
    this.allocationKw = kw === null ? null : Math.max(0, kw);
  }

  /** The standing allocation, or `null` if the district is on its interconnect. */
  getAllocation(): number | null {
    return this.allocationKw;
  }

  /** What the district may draw: the allocation if there is one, else the interconnect. */
  getSupplyKw(): number {
    return this.allocationKw ?? this.config.INTERCONNECT_KW;
  }

  // --- Readings -------------------------------------------------------------

  /** Demand at the last metered cycle, by class. Zero before the first one. */
  getDemand(): PowerDemand {
    return { ...this.demand };
  }

  /** Supply at the last metered cycle. Zero before the first one. */
  getMeteredSupplyKw(): number {
    return this.supplyKw;
  }

  /** Load shed at the last metered cycle. Zero when demand fitted. */
  getShedKw(): number {
    return this.shedKw;
  }

  /** True while this building is shed: dark if built, stalled if not. */
  isShed(houseId: number): boolean {
    return this.shedHouses.has(houseId);
  }

  /** Whether anything is shed at all. The cheap question, asked every tick. */
  get shedding(): boolean {
    return this.shedHouses.size > 0 || this.litCellsShed > 0;
  }

  /** Buildings shed at the last metered cycle. */
  getShedCount(): number {
    return this.shedHouses.size;
  }

  /** Lit cells shed at the last metered cycle. */
  getLitCellsShed(): number {
    return this.litCellsShed;
  }

  /**
   * What fraction of the estate's demand was actually delivered, in [0, 1].
   *
   * 1 when there is no facility on the map or when everything fitted, which is
   * why a district that has never hosted anything reads the same as one whose
   * data centre is running flat out — there is nothing to distinguish. The number
   * a facility's uptime is `min`'d from (`FacilityManager.meter`).
   */
  getFacilityFraction(): number {
    const demand = this.demand.facilities;
    if (demand <= 0) return 1;
    return Math.min(1, Math.max(0, (demand - this.facilityShedKw) / demand));
  }

  /** Kilowatts the estate was refused at the last metered cycle. */
  getFacilityShedKw(): number {
    return this.facilityShedKw;
  }

  /**
   * Cycles this building has spent shed, over its whole life.
   *
   * The quantity §12 puts in a mayor's face when a family leaves — *"The
   * Harrisons left: 41 brownout-hours"* — and the input §10's migration will sum
   * over. Read it before the house is removed; the entry goes when the house
   * does.
   */
  getDarkCycles(houseId: number): number {
    return this.darkCycles.get(houseId) ?? 0;
  }

  /** The lifetime totals. Monotone sums, so a season's flows are a subtraction. */
  getLedger() {
    return {
      meteredCycles: this.meteredCycles,
      shortfallCycles: this.shortfallCycles,
      deliveredKwCycles: this.deliveredKwCycles,
      shedKwCycles: this.shedKwCycles,
      darkHouseCycles: this.darkHouseCycles,
      facilityShedKwCycles: this.facilityShedKwCycles,
    };
  }

  // --- The meter ------------------------------------------------------------

  /**
   * Meter one budget cycle: sum the demand, shed what does not fit, record it.
   *
   * Called once per settled budget cycle — the district's own fiscal period,
   * which is cadence-invariant (`docs/simulation-cadence.md`), so a brownout is
   * the same length of world at 6 Hz as at 60. Metering per tick would be the
   * same arithmetic at ninety times the cost and would put a house's lights on a
   * rate the tick size decides.
   */
  meter(reading: PowerReading): void {
    const { HOUSEHOLD_KW, COMMERCIAL_KW, STREETLIGHT_KW, CONSTRUCTION_KW } = this.config;

    const demand: PowerDemand = {
      streets: reading.litCells * STREETLIGHT_KW,
      sites: reading.sites.length * CONSTRUCTION_KW,
      premises: reading.premises.length * COMMERCIAL_KW,
      homes: reading.homes.length * HOUSEHOLD_KW,
      facilities: Math.max(0, reading.facilityKw),
      total: 0,
    };
    demand.total = demand.streets + demand.sites + demand.premises +
                   demand.homes + demand.facilities;

    const supply = this.getSupplyKw();

    this.demand = demand;
    this.supplyKw = supply;
    this.shedHouses.clear();
    this.litCellsShed = 0;
    this.facilityShedKw = 0;
    this.shedKw = 0;

    let deficit = demand.total - supply;
    if (deficit > 0) {
      // Down `SHED_ORDER`, taking whole consumers until the deficit is covered.
      // The last one taken generally overshoots by less than its own draw, which
      // is what a circuit does: you cannot switch off four fifths of a house.
      for (const cls of SHED_ORDER) {
        if (deficit <= 0) break;
        deficit -= this.shedClass(cls, reading, deficit);
      }
    }

    // The rotation advances every metered cycle, shortfall or not, so a
    // shortfall that arrives and departs does not always land on the same
    // families.
    this.rotation++;

    const delivered = demand.total - this.shedKw;
    this.meteredCycles++;
    this.deliveredKwCycles += delivered;
    this.shedKwCycles += this.shedKw;
    if (this.shedKw > 0) this.shortfallCycles++;

    for (const houseId of this.sortedShedHouses()) {
      this.darkCycles.set(houseId, (this.darkCycles.get(houseId) ?? 0) + 1);
    }
    this.darkHouseCycles += this.shedHouses.size;
    this.facilityShedKwCycles += this.facilityShedKw;

    this.pruneDarkCycles(reading);
  }

  /**
   * Shed one class until the deficit is covered. Returns the kW taken off.
   *
   * Street lighting is a count of cells and comes off a cell at a time. Three
   * classes are buildings, and come off starting at `rotation` and wrapping, so
   * the same twelve houses are not dark every cycle of a long shortage.
   *
   * The facilities are the exception in both respects, and it is the one place in
   * this file where a class is not a list of whole consumers. §4's production rule
   * is `min(power, water, staffing) × capacity`: a facility short of power runs
   * *slower*, it does not switch off. So the estate is curtailed by exactly the
   * remaining deficit — no overshoot, no rota — and `FacilityManager` reads the
   * fraction rather than a list of which machine was unlucky.
   */
  private shedClass(cls: PowerClass, reading: PowerReading, deficit: number): number {
    if (cls === 'facilities') {
      const demand = this.demand.facilities;
      if (demand <= 0) return 0;
      const taken = Math.min(demand, deficit);
      this.facilityShedKw = taken;
      this.shedKw += taken;
      return taken;
    }

    if (cls === 'streets') {
      const unit = this.config.STREETLIGHT_KW;
      if (unit <= 0 || reading.litCells <= 0) return 0;
      const cells = Math.min(reading.litCells, Math.ceil(deficit / unit));
      this.litCellsShed = cells;
      const taken = cells * unit;
      this.shedKw += taken;
      return taken;
    }

    const unit = cls === 'sites' ? this.config.CONSTRUCTION_KW
      : cls === 'premises' ? this.config.COMMERCIAL_KW
      : this.config.HOUSEHOLD_KW;
    const ids = cls === 'sites' ? reading.sites
      : cls === 'premises' ? reading.premises
      : reading.homes;

    if (unit <= 0 || ids.length === 0) return 0;

    const count = Math.min(ids.length, Math.ceil(deficit / unit));
    const start = ids.length > 0 ? this.rotation % ids.length : 0;
    for (let i = 0; i < count; i++) {
      this.shedHouses.add(ids[(start + i) % ids.length]);
    }
    const taken = count * unit;
    this.shedKw += taken;
    return taken;
  }

  /** Shed ids, ascending. Iteration that writes the ledger cannot use `Set` order. */
  private sortedShedHouses(): number[] {
    return Array.from(this.shedHouses).sort((a, b) => a - b);
  }

  /**
   * Forget buildings that are no longer on the map.
   *
   * A house that burned through forty dark cycles and then vanished has nobody
   * to report them to, and an unbounded map keyed by a monotone id would be a
   * slow leak in a world designed to run for sixty-four seasons. Migration (§10)
   * reads a family's figure while the house is still standing.
   */
  private pruneDarkCycles(reading: PowerReading): void {
    if (this.darkCycles.size === 0) return;
    const present = new Set<number>();
    for (const id of reading.sites) present.add(id);
    for (const id of reading.premises) present.add(id);
    for (const id of reading.homes) present.add(id);

    for (const id of Array.from(this.darkCycles.keys()).sort((a, b) => a - b)) {
      if (!present.has(id)) this.darkCycles.delete(id);
    }
  }

  // --- State ---------------------------------------------------------------

  save(): PowerSnapshot {
    return {
      allocationKw: this.allocationKw,
      rotation: this.rotation,
      demand: { ...this.demand },
      supplyKw: this.supplyKw,
      shedKw: this.shedKw,
      shedHouses: this.sortedShedHouses(),
      litCellsShed: this.litCellsShed,
      facilityShedKw: this.facilityShedKw,
      darkCycles: Array.from(this.darkCycles.keys())
        .sort((a, b) => a - b)
        .map((id) => [id, this.darkCycles.get(id)!] as [number, number]),
      meteredCycles: this.meteredCycles,
      shortfallCycles: this.shortfallCycles,
      deliveredKwCycles: this.deliveredKwCycles,
      shedKwCycles: this.shedKwCycles,
      darkHouseCycles: this.darkHouseCycles,
      facilityShedKwCycles: this.facilityShedKwCycles,
    };
  }

  load(state: PowerSnapshot): void {
    this.allocationKw = state.allocationKw;
    this.rotation = state.rotation;
    this.demand = { ...state.demand };
    this.supplyKw = state.supplyKw;
    this.shedKw = state.shedKw;
    this.shedHouses.clear();
    for (const id of state.shedHouses) this.shedHouses.add(id);
    this.litCellsShed = state.litCellsShed;
    this.facilityShedKw = state.facilityShedKw;
    this.darkCycles.clear();
    for (const [id, cycles] of state.darkCycles) this.darkCycles.set(id, cycles);
    this.meteredCycles = state.meteredCycles;
    this.shortfallCycles = state.shortfallCycles;
    this.deliveredKwCycles = state.deliveredKwCycles;
    this.shedKwCycles = state.shedKwCycles;
    this.darkHouseCycles = state.darkHouseCycles;
    this.facilityShedKwCycles = state.facilityShedKwCycles;
  }

  /**
   * Order-stable view of the connection, for hashing — and nothing before the
   * first cycle is metered.
   *
   * A district that has never metered anything has an untouched meter, and the
   * key is omitted rather than written as a bag of zeroes. That is the move the
   * Chronicle and the command queue both make (`SurfaceSim.canonicalState()`),
   * and it is what lets a flow arrive without moving a golden master recorded
   * before it existed: every corpus entry shorter than one budget cycle is
   * unaffected, and every entry whose district never ran short hashes on the
   * ledger alone.
   *
   * The current shed set is in, not just the totals. Two districts that agree on
   * every figure and disagree about which house is dark diverge on the next
   * building step.
   */
  canonicalState(): Canonical | undefined {
    if (this.meteredCycles === 0 && this.allocationKw === null) return undefined;

    // The facility terms are left out while they are zero, which is the same move
    // this method already makes for the whole meter one level up: a district with
    // nothing federal on it hashes as though facilities did not exist. That is why
    // ADR-015 moved no golden master — every recorded run in this repository is a
    // district nobody sited anything on.
    const demand: Record<string, Canonical> = {
      streets: this.demand.streets,
      sites: this.demand.sites,
      premises: this.demand.premises,
      homes: this.demand.homes,
      total: this.demand.total,
    };
    if (this.demand.facilities > 0) demand.facilities = this.demand.facilities;

    const state: Record<string, Canonical> = {
      allocationKw: this.allocationKw,
      rotation: this.rotation,
      demand,
      supplyKw: this.supplyKw,
      shedKw: this.shedKw,
      shedHouses: this.sortedShedHouses(),
      litCellsShed: this.litCellsShed,
      darkCycles: Array.from(this.darkCycles.keys())
        .sort((a, b) => a - b)
        .map((id) => `${id}:${this.darkCycles.get(id)}`),
      meteredCycles: this.meteredCycles,
      shortfallCycles: this.shortfallCycles,
      deliveredKwCycles: this.deliveredKwCycles,
      shedKwCycles: this.shedKwCycles,
      darkHouseCycles: this.darkHouseCycles,
    };
    if (this.facilityShedKw > 0) state.facilityShedKw = this.facilityShedKw;
    if (this.facilityShedKwCycles > 0) {
      state.facilityShedKwCycles = this.facilityShedKwCycles;
    }
    return state;
  }
}
